import { Suspense, useEffect, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import type { PayloadError } from 'relay-runtime';
import {
    BlockStack,
    Box,
    Button,
    InlineStack,
    PageHeader,
    useToast,
    type SelectOption,
} from '@pv-frontend/ui-components-web';
import { crawlRunsQuery } from '../relay/crawl-runs-query';
import type { crawlRunsQuery as CrawlRunsQuery } from '../relay/__generated__/crawlRunsQuery.graphql';
import { crawlSitesQuery } from '../relay/crawl-sites-query';
import type { crawlSitesQuery as CrawlSitesQuery } from '../relay/__generated__/crawlSitesQuery.graphql';
import { requestListingCrawlMutation } from '../relay/request-listing-crawl-mutation';
import { usePollingFetchKey } from '../relay/use-polling-fetch-key';
import { isActiveStatus, type CrawlRunScope } from './crawl-format';
import { CrawlRunsList, type CrawlRunRow } from './crawls-runs-list';
import { RunCrawlModal } from './run-crawl-modal';
import { LoadingContent } from './loading-content';

/**
 * Listings → Crawls (pv-frontend#163). The operator surface for the crawl engine:
 * the persisted run history (manual + nightly) and the "Run crawl" trigger. Reads
 * the `Apis.Admin` crawl surface and, since that host has no GraphQL
 * subscriptions, polls (~5s) while any run is still in flight — see
 * `CrawlsResults`. The header is static; the data-driven body suspends on its own.
 */
export function CrawlsScreen() {
    return (
        <Box padding="600" maxWidth={1080}>
            <BlockStack gap="500">
                <PageHeader
                    title="Crawls"
                    description="Operator-triggered and nightly listings crawls, newest first — trigger a run and see the persisted per-agent breakdown of every one."
                />
                <Suspense fallback={<LoadingContent />}>
                    <CrawlsResults />
                </Suspense>
            </BlockStack>
        </Box>
    );
}

const ALREADY_RUNNING = 'A crawl is already running. Wait for it to finish before starting another.';

type MutationResponse = { requestListingCrawl?: { id: string; status: string } };

/**
 * The data-driven body: the polled runs list plus the trigger modal. `active`
 * (any run `Queued`/`Running`) drives both the poll cadence and the disabled
 * trigger — at most one run runs platform-wide, so a second is blocked up front
 * (the mutation's idempotency is the backstop). A manual refetch bump makes a
 * newly-queued run appear immediately rather than on the next tick.
 */
function CrawlsResults() {
    const toast = useToast();
    const [active, setActive] = useState(false);
    const [refreshBump, setRefreshBump] = useState(0);
    const pollKey = usePollingFetchKey(active);

    const runsData = useLazyLoadQuery<CrawlRunsQuery>(
        crawlRunsQuery,
        {},
        { fetchKey: pollKey + refreshBump, fetchPolicy: 'store-and-network' },
    );
    const sitesData = useLazyLoadQuery<CrawlSitesQuery>(
        crawlSitesQuery,
        {},
        { fetchPolicy: 'store-or-network' },
    );

    const runs: CrawlRunRow[] = runsData.crawlRuns.map((run) => ({
        id: run.id,
        trigger: run.trigger,
        requestedByOperator: run.requestedByOperator,
        scope: run.scope,
        status: run.status,
        requestedUtc: run.requestedUtc,
        startedUtc: run.startedUtc ?? null,
        completedUtc: run.completedUtc ?? null,
        siteId: run.siteId ?? null,
        sitesCrawled: run.sitesCrawled,
        sitesPartiallyCrawled: run.sitesPartiallyCrawled,
        listingsAdded: run.listingsAdded,
        listingsUpdated: run.listingsUpdated,
        listingsUnchanged: run.listingsUnchanged,
        listingsMissed: run.listingsMissed,
        listingsDelisted: run.listingsDelisted,
        rejectsQueued: run.rejectsQueued,
    }));

    const anyActive = runs.some((run) => isActiveStatus(run.status));
    useEffect(() => {
        setActive(anyActive);
    }, [anyActive]);

    const siteOptions: SelectOption[] = sitesData.crawlSites.map((site) => ({
        value: site.id,
        label: site.isCrawlable
            ? `${site.name} · ${site.areasServed}`
            : `${site.name} — not crawlable`,
        disabled: !site.isCrawlable,
    }));

    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState<CrawlRunScope>('ALL_SITES');
    const [siteId, setSiteId] = useState('');
    const [siteError, setSiteError] = useState<string | undefined>(undefined);
    const [commit, submitting] = useMutation(requestListingCrawlMutation);

    const openModal = () => {
        setScope('ALL_SITES');
        setSiteId('');
        setSiteError(undefined);
        setOpen(true);
    };

    const confirm = () => {
        if (scope === 'SINGLE_SITE' && !siteId) {
            setSiteError('Choose a site to crawl.');
            return;
        }
        setSiteError(undefined);
        commit({
            variables: { scope, siteId: scope === 'SINGLE_SITE' ? siteId : null },
            onCompleted: (response: MutationResponse, errors: PayloadError[] | null) => {
                if (errors && errors.length > 0) {
                    toast.error('Could not start the crawl', 'The request was rejected. Please try again.');
                    return;
                }
                const status = response.requestListingCrawl?.status;
                setOpen(false);
                setActive(true); // start polling immediately
                setRefreshBump((bump) => bump + 1); // and refetch now so the run shows
                // The mutation is idempotent w.r.t. the active run: a non-Queued status back means a
                // run was already in flight and we adopted it rather than stacking a new one.
                if (status && status !== 'QUEUED') {
                    toast.success('A crawl is already running', 'Showing the run already in flight.');
                } else {
                    toast.success('Crawl queued');
                }
            },
            onError: () => {
                toast.error('Could not start the crawl', 'The request was rejected. Please try again.');
            },
        });
    };

    return (
        <BlockStack gap="400">
            <InlineStack align="flex-end">
                <Button variant="primary" onPress={openModal} disabled={anyActive}>
                    Run crawl
                </Button>
            </InlineStack>

            <CrawlRunsList runs={runs} />

            <RunCrawlModal
                open={open}
                onClose={() => setOpen(false)}
                scope={scope}
                onScopeChange={setScope}
                siteOptions={siteOptions}
                siteId={siteId}
                onSiteChange={(next) => {
                    setSiteId(next);
                    setSiteError(undefined);
                }}
                onConfirm={confirm}
                submitting={submitting}
                activeRunNotice={anyActive ? ALREADY_RUNNING : null}
                siteError={siteError}
            />
        </BlockStack>
    );
}
